import { Box, svgIconClasses, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FavoritePresetsSection,
  VIEW_VALIDATORS,
} from '@/features/savedPresets';
import { ROUTES } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import { PresetsIcon } from '@/shared/ui';
import {
  ExploreSection,
  useAllowedExploreViews,
  useIsAnyExploreViewPermitted,
} from '../../../../model/exploreViews';

const PanelRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible?: boolean }>(({ visible }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 280,
  zIndex: 1300,
  backgroundColor: '#f1f3f8',
  borderLeft: '1px solid #dddfe7',
  borderTopRightRadius: 16,
  borderBottomRightRadius: 16,
  boxShadow: '0px 2px 6px 0px rgba(50, 50, 71, 0.16)',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  display: 'flex',
  flexDirection: 'column',
  transform: visible ? 'translateX(0)' : 'translateX(-68px)',
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  transition: 'transform 0.2s ease-out, opacity 0.15s ease-out',
}));

const Section = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: `${theme.spacing(2)} ${theme.spacing(1.5)}`,
  borderBottom: '1px solid #dddfe7',
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 'normal',
  color: '#7e828f',
  paddingLeft: theme.spacing(1),
  textTransform: 'uppercase',
}));

const NavItemLink = styled(NavLink)(({ theme }) => ({
  height: 40,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `0 ${theme.spacing(1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  textDecoration: 'none',
  color: '#343844',
  flexShrink: 0,

  '&.active': {
    backgroundColor: '#e7ecfa',

    [`& .${svgIconClasses.root}`]: {
      color: theme.palette.primary.main,
    },
  },

  '&:hover:not(.active)': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const NavItemIcon = styled(Box)(() => ({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: '#7E828F',

  '& svg': {
    width: 24,
    height: 24,
  },
}));

const NavItemLabel = styled('span')(() => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '18px',
  color: 'inherit',
  flex: '1 0 0',
  minWidth: 0,
}));

function NavItem({
  to,
  icon,
  label,
  tag,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  tag?: ReactNode;
}) {
  const handleClick = () => {
    Analytics.sendFeatureUsage('view_navigation', 'navigation_drawer_item', {
      view: to,
    });
  };

  return (
    <NavItemLink to={to} onClick={handleClick}>
      <NavItemIcon>{icon}</NavItemIcon>
      <NavItemLabel>{label}</NavItemLabel>
      {tag}
    </NavItemLink>
  );
}

function SectionGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <SectionHeader>{title}</SectionHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
}

export function ExplorePanel({ visible }: { visible: boolean }) {
  const allowedViews = useAllowedExploreViews();
  const isAnyDataViewAllowed = useIsAnyExploreViewPermitted();

  const cohortViews = allowedViews.filter(
    ({ section }) => section === ExploreSection.cohort,
  );
  const calendarViews = allowedViews.filter(
    ({ section }) => section === ExploreSection.calendar,
  );

  return (
    <PanelRoot visible={visible}>
      <Section sx={{ pt: 3 }}>
        {cohortViews.length > 0 && (
          <SectionGroup title={ExploreSection.cohort}>
            {cohortViews.map(({ route, label, Icon }) => (
              <NavItem key={route} to={route} icon={<Icon />} label={label} />
            ))}
          </SectionGroup>
        )}
        {calendarViews.length > 0 && (
          <SectionGroup title={ExploreSection.calendar}>
            {calendarViews.map(({ route, label, Icon }) => (
              <NavItem key={route} to={route} icon={<Icon />} label={label} />
            ))}
          </SectionGroup>
        )}
      </Section>

      {isAnyDataViewAllowed && (
        <>
          <Section>
            <NavItem
              to={ROUTES.presetsPage}
              icon={<PresetsIcon />}
              label="All Presets"
            />
          </Section>
          <Section
            sx={{
              borderBottom: 'none',
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <FavoritePresetsSection validators={VIEW_VALIDATORS} />
          </Section>
        </>
      )}
    </PanelRoot>
  );
}
